// src/components/Login/LoginForm.tsx
import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Mail, Lock, LogIn } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useIsMobile } from '../../utils/isMobile'
import logo from '../../assets/damelio_logo_blanco.png'
import logo2 from '../../assets/damelio_texto_blanco.png'

interface LoginFormProps {
  email: string
  password: string
  setEmail: (email: string) => void
  setPassword: (password: string) => void
  handleSubmit: () => void
  error: string
  toggleForm: () => void
  toggleForgotPassword: () => void
}

const LoginForm: React.FC<LoginFormProps> = ({
  email,
  password,
  setEmail,
  setPassword,
  handleSubmit,
  error,
  toggleForm,
  toggleForgotPassword,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const isMobile = useIsMobile()

  return (
    <div className="w-full max-w-sm mx-auto space-y-6 text-white px-4">
      {/* Advertencia móvil */}
      {isMobile && (
        <p className="bg-yellow-500 text-black text-center text-sm font-medium p-2 rounded">
          {t('login.mobileWarning')}
        </p>
      )}

      {/* Logos */}
      <div
        className="text-center cursor-pointer transition-transform duration-300 hover:scale-105"
        onClick={() => navigate('/')}
      >
        <img src={logo} alt={t('login.altLogo')} className="mx-auto h-28" />
        <img
          src={logo2}
          alt={t('login.altLogo2')}
          className="mx-auto h-16 mt-2"
        />
      </div>

      {/* Form */}
      <form
        onSubmit={(e) => {
          e.preventDefault()
          handleSubmit()
        }}
        className="space-y-4"
      >
        {/* Email */}
        <div>
          <label
            htmlFor="email"
            className="block text-sm font-medium text-gray-200"
          >
            {t('login.labels.email')}
          </label>
          <div className="relative mt-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Mail className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('login.placeholders.email')}
              required
              className="
                block w-full pl-10 pr-3 py-2
                bg-gray-800 border border-gray-700
                rounded-lg text-white placeholder-gray-500
                focus:outline-none focus:ring-2 focus:ring-blue-500
                focus:border-blue-500 transition
              "
            />
          </div>
        </div>

        {/* Password */}
        <div>
          <label
            htmlFor="password"
            className="block text-sm font-medium text-gray-200"
          >
            {t('login.labels.password')}
          </label>
          <div className="relative mt-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Lock className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('login.placeholders.password')}
              required
              className="
                block w-full pl-10 pr-3 py-2
                bg-gray-800 border border-gray-700
                rounded-lg text-white placeholder-gray-500
                focus:outline-none focus:ring-2 focus:ring-blue-500
                focus:border-blue-500 transition
              "
            />
          </div>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          className="
            w-full flex items-center justify-center gap-2
            bg-gradient-to-r from-blue-500 to-indigo-600
            hover:from-blue-600 hover:to-indigo-700
            text-white font-semibold rounded-lg
            shadow-lg shadow-indigo-900/50 transition-transform
            transform hover:scale-105
            px-4 py-2
          "
        >
          <LogIn className="h-5 w-5" />
          {t('login.buttons.login')}
        </button>
      </form>

      {/* Links */}
      <div className="text-center space-y-1">
        <p className="text-sm text-gray-400">
          {t('login.noAccount')}
          <span
            onClick={toggleForm}
            className="ml-1 text-blue-400 hover:underline cursor-pointer"
          >
            {t('login.register')}
          </span>
        </p>
        <p className="text-sm text-gray-400">
          <span
            onClick={toggleForgotPassword}
            className="text-blue-400 hover:underline cursor-pointer"
          >
            {t('login.forgotPassword')}
          </span>
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <p className="text-red-500 text-center mt-2">{error}</p>
      )}
    </div>
  )
}

export default LoginForm
